import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { Build as AdvancedIcon } from '@mui/icons-material';
import { KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderBanner from '../components/PageHeaderBanner';
import {
    computeDueDatePreview,
    ContractCorrectionsSummary,
    ContractTermEditor,
    CorrectionAction,
    currentTermValue,
    describeContractStatus,
    formatRonAmount,
    getContractTermEditors,
    getCorrectionActions,
    normalizeConsignmentCode,
    parseExchangeRateInput,
    parseTermValue,
} from '../domain/corrections';
import useRequest from '../hooks/useRequest';

/** An operation waiting for the operator to confirm it in the dialog. */
interface PendingOperation {
    /** i18n suffix under `advanced.corrections.confirm` / `.success`. */
    key: string;
    endpoint: string;
    params: Record<string, unknown>;
    /** Interpolated into the confirmation text as `{{subject}}`. */
    subject: string;
    /** Whether the loaded contract summary has to be reloaded afterwards. */
    refreshesContract: boolean;
}

const Advanced = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const [activeTab, setActiveTab] = useState(0);
    const [contractNrInput, setContractNrInput] = useState('');
    const [summary, setSummary] = useState<ContractCorrectionsSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [pending, setPending] = useState<PendingOperation | null>(null);
    const [termEditor, setTermEditor] = useState<ContractTermEditor | null>(null);
    const [termValue, setTermValue] = useState('');
    const [termError, setTermError] = useState('');
    const [rateDate, setRateDate] = useState('');
    const [rateValue, setRateValue] = useState('');
    const [consignmentCode, setConsignmentCode] = useState('');

    const actions = getCorrectionActions(summary);
    const termEditors = getContractTermEditors();
    const contractLabel = String(summary?.contractNumber ?? summary?.contractNr ?? '');

    const loadSummary = async (contractNr: number) => {
        setLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            const response = await request<{
                success: boolean;
                data: ContractCorrectionsSummary;
                message?: string;
            }>({
                path: `/corrections/contract/${contractNr}`,
                method: 'GET',
            });
            if (response.success && response.data) {
                setSummary(response.data);
            } else {
                setSummary(null);
                setError(response.message ?? t('advanced.corrections.loadFailed'));
            }
        } catch (loadError) {
            setSummary(null);
            setError(
                loadError instanceof Error
                    ? loadError.message
                    : t('advanced.corrections.loadFailed'),
            );
        } finally {
            setLoading(false);
        }
    };

    const handleLoadClick = () => {
        const contractNr = Number.parseInt(contractNrInput, 10);
        if (!Number.isInteger(contractNr) || contractNr <= 0) {
            setSummary(null);
            setError(t('advanced.corrections.invalidContractNr'));
            return;
        }
        loadSummary(contractNr);
    };

    const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleLoadClick();
        }
    };

    /**
     * Post one correction and report it. Contract-scoped operations reload the
     * summary first, because `loadSummary` resets the alerts.
     */
    const runOperation = async (operation: PendingOperation) => {
        setProcessing(true);
        setError('');
        setSuccessMessage('');
        try {
            const response = await request<{
                success: boolean;
                data?: Record<string, unknown>;
                message?: string;
            }>({
                path: operation.endpoint,
                method: 'POST',
                params: operation.params,
            });
            if (response.success) {
                if (operation.refreshesContract && summary) {
                    await loadSummary(summary.contractNr);
                }
                setSuccessMessage(
                    t(`advanced.corrections.success.${operation.key}`, {
                        subject: operation.subject,
                        ...response.data,
                    }),
                );
                return true;
            }
            setError(response.message ?? t('advanced.corrections.actionFailed'));
        } catch (actionError) {
            setError(
                actionError instanceof Error
                    ? actionError.message
                    : t('advanced.corrections.actionFailed'),
            );
        } finally {
            setProcessing(false);
            setPending(null);
        }
        return false;
    };

    const confirmReversal = (action: CorrectionAction) => {
        if (!summary) {
            return;
        }
        setPending({
            key: action.key,
            endpoint: action.endpoint,
            params: { contractNr: summary.contractNr },
            subject: contractLabel,
            refreshesContract: true,
        });
    };

    const openTermEditor = (editor: ContractTermEditor) => {
        setTermEditor(editor);
        setTermValue(currentTermValue(summary, editor.key));
        setTermError('');
    };

    const saveTerm = async () => {
        if (!termEditor || !summary) {
            return;
        }
        const value = parseTermValue(termEditor, termValue);
        if (value === null) {
            setTermError(
                t('advanced.corrections.invalidValue', {
                    min: termEditor.min,
                    max: termEditor.max,
                }),
            );
            return;
        }
        const saved = await runOperation({
            key: termEditor.key,
            endpoint: termEditor.endpoint,
            params: { contractNr: summary.contractNr, [termEditor.field]: value },
            subject: contractLabel,
            refreshesContract: true,
        });
        if (saved) {
            setTermEditor(null);
        }
    };

    const confirmExchangeRate = () => {
        const rate = parseExchangeRateInput(rateValue);
        if (!rateDate || rate === null) {
            setError(t('advanced.corrections.exchangeRate.invalid'));
            return;
        }
        setPending({
            key: 'setExchangeRate',
            endpoint: '/corrections/set-exchange-rate',
            params: { rateDate, exchangeRate: rate },
            subject: rateDate,
            refreshesContract: false,
        });
    };

    const confirmConsignmentReversal = () => {
        const code = normalizeConsignmentCode(consignmentCode);
        if (!code) {
            setError(t('advanced.corrections.consignment.invalid'));
            return;
        }
        setPending({
            key: 'reverseConsignmentSale',
            endpoint: '/corrections/reverse-consignment-sale',
            params: { code },
            subject: code,
            refreshesContract: false,
        });
    };

    const termPreview =
        termEditor?.key === 'setDuration'
            ? computeDueDatePreview(summary?.contractDate, parseTermValue(termEditor, termValue))
            : null;

    return (
        <Box sx={{ p: 2 }} data-testid="advanced-page">
            <PageHeaderBanner
                icon={<AdvancedIcon />}
                title={t('advanced.title')}
                subtitle={t('advanced.subtitle')}
                accentColor="#db2777"
            />

            <Tabs
                value={activeTab}
                onChange={(_event, newValue: number) => setActiveTab(newValue)}
                sx={{ mb: 2 }}
            >
                <Tab label={t('advanced.tabs.corrections')} data-testid="corrections-tab" />
            </Tabs>

            {activeTab === 0 && (
                <Box data-testid="corrections-panel">
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
                        <TextField
                            size="small"
                            label={t('advanced.corrections.contractNr')}
                            value={contractNrInput}
                            onChange={event => setContractNrInput(event.target.value)}
                            onKeyDown={handleInputKeyDown}
                            slotProps={{
                                htmlInput: { 'data-testid': 'corrections-contract-input' },
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleLoadClick}
                            disabled={loading}
                            data-testid="corrections-load-button"
                        >
                            {t('advanced.corrections.load')}
                        </Button>
                        {loading && <CircularProgress size={20} />}
                    </Stack>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} data-testid="corrections-error">
                            {error}
                        </Alert>
                    )}
                    {successMessage && (
                        <Alert severity="success" sx={{ mb: 2 }} data-testid="corrections-success">
                            {successMessage}
                        </Alert>
                    )}

                    <Stack spacing={2}>
                        {summary && (
                            <>
                                <Paper sx={{ p: 2 }} data-testid="corrections-summary">
                                    <Stack
                                        direction="row"
                                        spacing={3}
                                        alignItems="center"
                                        flexWrap="wrap"
                                    >
                                        <Typography variant="subtitle1">
                                            {t('advanced.corrections.contract')}{' '}
                                            <strong>
                                                {summary.contractNumber ?? summary.contractNr}
                                            </strong>
                                        </Typography>
                                        <Typography>{summary.clientName}</Typography>
                                        <Chip
                                            size="small"
                                            label={t(
                                                `advanced.corrections.status.${describeContractStatus(summary.status)}`,
                                            )}
                                            data-testid="corrections-status"
                                        />
                                        <Typography>
                                            {t('advanced.corrections.loanAmount')}:{' '}
                                            {formatRonAmount(summary.loanAmountRon)} LEI
                                        </Typography>
                                        <Typography>
                                            {t('advanced.corrections.expiration')}:{' '}
                                            {summary.expirationDate ?? '-'}
                                        </Typography>
                                    </Stack>
                                </Paper>

                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                    {actions.map(action => (
                                        <Button
                                            key={action.key}
                                            variant="outlined"
                                            color="warning"
                                            disabled={!action.enabled || processing}
                                            onClick={() => confirmReversal(action)}
                                            data-testid={`corrections-action-${action.key}`}
                                        >
                                            {t(`advanced.corrections.actions.${action.key}`)}
                                        </Button>
                                    ))}
                                </Stack>

                                <Paper sx={{ p: 2 }} data-testid="corrections-terms">
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {t('advanced.corrections.terms')}
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        spacing={3}
                                        alignItems="center"
                                        flexWrap="wrap"
                                        sx={{ mb: 2 }}
                                    >
                                        <Typography data-testid="corrections-term-commission">
                                            {t('advanced.corrections.dailyCommission')}:{' '}
                                            {summary.dailyInterestRate}
                                        </Typography>
                                        <Typography data-testid="corrections-term-duration">
                                            {t('advanced.corrections.duration')}:{' '}
                                            {summary.durationDays ?? '-'}
                                        </Typography>
                                        <Typography data-testid="corrections-term-due-date">
                                            {t('advanced.corrections.dueDate')}:{' '}
                                            {summary.originalDueDate ?? '-'}
                                        </Typography>
                                        <Typography data-testid="corrections-term-discount">
                                            {t('advanced.corrections.discount')}:{' '}
                                            {summary.standardDiscountPercent ?? 0}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={2} flexWrap="wrap">
                                        {termEditors.map(editor => (
                                            <Button
                                                key={editor.key}
                                                variant="outlined"
                                                disabled={processing}
                                                onClick={() => openTermEditor(editor)}
                                                data-testid={`corrections-edit-${editor.key}`}
                                            >
                                                {t(
                                                    `advanced.corrections.termActions.${editor.key}`,
                                                )}
                                            </Button>
                                        ))}
                                    </Stack>
                                </Paper>

                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {t('advanced.corrections.invoices')}
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small" data-testid="corrections-invoices">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        {t('advanced.corrections.invoiceNumber')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {t('advanced.corrections.invoiceDate')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {t('advanced.corrections.invoiceType')}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {t('advanced.corrections.amount')}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summary.invoices.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={4}>
                                                            {t('advanced.corrections.noInvoices')}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {summary.invoices.map(invoice => (
                                                    <TableRow key={invoice.id}>
                                                        <TableCell>
                                                            {invoice.invoiceNumber}
                                                        </TableCell>
                                                        <TableCell>{invoice.invoiceDate}</TableCell>
                                                        <TableCell>{invoice.invoiceType}</TableCell>
                                                        <TableCell align="right">
                                                            {formatRonAmount(invoice.amount)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>

                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {t('advanced.corrections.extensions')}
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small" data-testid="corrections-extensions">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        {t('advanced.corrections.extensionNumber')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {t('advanced.corrections.extensionDate')}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {t('advanced.corrections.amount')}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summary.extensions.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={3}>
                                                            {t('advanced.corrections.noExtensions')}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {summary.extensions.map(extension => (
                                                    <TableRow key={extension.id}>
                                                        <TableCell>
                                                            {extension.extensionNumber}
                                                        </TableCell>
                                                        <TableCell>
                                                            {extension.extensionDate}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatRonAmount(extension.amountRon)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </>
                        )}

                        <Paper sx={{ p: 2 }} data-testid="corrections-exchange-rate">
                            <Typography variant="subtitle2">
                                {t('advanced.corrections.exchangeRate.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('advanced.corrections.exchangeRate.hint')}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                <TextField
                                    size="small"
                                    type="date"
                                    label={t('advanced.corrections.exchangeRate.date')}
                                    value={rateDate}
                                    onChange={event => setRateDate(event.target.value)}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        htmlInput: { 'data-testid': 'corrections-rate-date' },
                                    }}
                                />
                                <TextField
                                    size="small"
                                    label={t('advanced.corrections.exchangeRate.rate')}
                                    value={rateValue}
                                    onChange={event => setRateValue(event.target.value)}
                                    slotProps={{
                                        htmlInput: { 'data-testid': 'corrections-rate-value' },
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    disabled={processing}
                                    onClick={confirmExchangeRate}
                                    data-testid="corrections-rate-apply"
                                >
                                    {t('advanced.corrections.exchangeRate.apply')}
                                </Button>
                            </Stack>
                        </Paper>

                        <Paper sx={{ p: 2 }} data-testid="corrections-consignment">
                            <Typography variant="subtitle2">
                                {t('advanced.corrections.consignment.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('advanced.corrections.consignment.hint')}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                <TextField
                                    size="small"
                                    label={t('advanced.corrections.consignment.code')}
                                    value={consignmentCode}
                                    onChange={event => setConsignmentCode(event.target.value)}
                                    slotProps={{
                                        htmlInput: {
                                            'data-testid': 'corrections-consignment-code',
                                        },
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    disabled={processing}
                                    onClick={confirmConsignmentReversal}
                                    data-testid="corrections-consignment-reverse"
                                >
                                    {t('advanced.corrections.consignment.reverse')}
                                </Button>
                            </Stack>
                        </Paper>
                    </Stack>
                </Box>
            )}

            <Dialog open={termEditor !== null} onClose={() => setTermEditor(null)}>
                <DialogTitle>
                    {termEditor && t(`advanced.corrections.termActions.${termEditor.key}`)}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin="dense"
                        label={
                            termEditor && t(`advanced.corrections.termActions.${termEditor.key}`)
                        }
                        value={termValue}
                        onChange={event => setTermValue(event.target.value)}
                        slotProps={{ htmlInput: { 'data-testid': 'corrections-term-input' } }}
                    />
                    {termPreview && (
                        <Typography sx={{ mt: 1 }} data-testid="corrections-term-preview">
                            {t('advanced.corrections.newDueDate')}: {termPreview}
                        </Typography>
                    )}
                    {termError && (
                        <Alert severity="error" sx={{ mt: 2 }} data-testid="corrections-term-error">
                            {termError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTermEditor(null)} disabled={processing}>
                        {t('advanced.corrections.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={processing}
                        onClick={saveTerm}
                        data-testid="corrections-term-save"
                    >
                        {t('advanced.corrections.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={pending !== null} onClose={() => setPending(null)}>
                <DialogTitle>{t('advanced.corrections.confirmTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {pending &&
                            t(`advanced.corrections.confirm.${pending.key}`, {
                                contract: pending.subject,
                                subject: pending.subject,
                            })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPending(null)} disabled={processing}>
                        {t('advanced.corrections.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        disabled={processing}
                        onClick={() => pending && runOperation(pending)}
                        data-testid="corrections-confirm-button"
                    >
                        {t('advanced.corrections.confirmButton')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Advanced;
